/**
 * Venttra - Guides collection schema
 */
import { buildCollection } from "@firecms/core";
import { editorOrAdminPermissions } from "../permissions";
import { createGuidesLocalizationReviewView } from "../components/LocalizationReviewView";
import { CONTENT_LOCALES, LOCALE_LABELS } from "../constants";
import { collectGuideCompletenessIssues } from "../lib/completenessValidation";
import { CONTENT_STATUS_ENUM } from "../lib/contentStatus";
import {
  STATUS_FIELD_DESCRIPTION,
  publishedSnapshotProperties,
} from "../lib/publishedSnapshotProperties";
import { createStatusPreSave } from "../lib/statusPreSave";

export const guidesCollection = buildCollection({
  id: "guides",
  name: "Guides",
  singularName: "Guide",
  path: "guides",
  icon: "MenuBook",
  group: "Content",
  permissions: editorOrAdminPermissions,
  entityViews: [createGuidesLocalizationReviewView()],
  callbacks: {
    onPreSave: createStatusPreSave({
      collectCompleteness: collectGuideCompletenessIssues,
      entityLabel: "guide",
    }),
  },
  properties: {
    title: {
      name: "Title",
      dataType: "map",
      description: "Localized title: { en, de, fr, it }",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const },
        ])
      ),
    },
    slug: {
      name: "Slug",
      dataType: "string",
      description: "URL-safe slug (e.g. emerald-isle)",
      validation: { required: true },
    },
    description: {
      name: "Description",
      dataType: "map",
      description: "Localized description",
      properties: Object.fromEntries(
        CONTENT_LOCALES.map((locale) => [
          locale,
          { name: LOCALE_LABELS[locale], dataType: "string" as const, markdown: true },
        ])
      ),
    },
    associated_articles: {
      name: "Associated Articles",
      dataType: "array",
      of: { dataType: "reference", path: "articles" },
      description: "Child articles (many-to-many)",
    },
    status: {
      name: "Status",
      dataType: "string",
      enumValues: CONTENT_STATUS_ENUM,
      description: STATUS_FIELD_DESCRIPTION,
    },
    author_id: {
      name: "Author",
      dataType: "reference",
      path: "authors",
      description: "Author of the guide (optional)",
    },
    ...publishedSnapshotProperties,
    created_at: {
      name: "Created At",
      dataType: "date",
      autoValue: "on_create",
    },
    updated_at: {
      name: "Updated At",
      dataType: "date",
      autoValue: "on_update",
    },
  },
});
